import type { CSSProperties, FC, ForwardRefExoticComponent, HTMLAttributes, ReactNode, RefAttributes } from 'react';
import { forwardRef } from 'react';
import clsx from 'clsx';
import styles from './Card.module.css';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  variant?: 'default' | 'outlined' | 'elevated' | 'flat';
  padding?: string | boolean;
  hoverable?: boolean;
}

interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  noPadding?: boolean;
}

interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  justify?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around';
}

interface CardComponent extends ForwardRefExoticComponent<CardProps & RefAttributes<HTMLDivElement>> {
  Header: FC<CardHeaderProps>;
  Body: FC<CardBodyProps>;
  Footer: FC<CardFooterProps>;
}

const CardHeader: FC<CardHeaderProps> = ({ children, className, ...props }) => (
  <div className={clsx(styles.header, className)} {...props}>
    {children}
  </div>
);

const CardBody: FC<CardBodyProps> = ({ children, noPadding = false, className, ...props }) => (
  <div className={clsx(styles.body, className)} data-no-padding={noPadding ? 'true' : undefined} {...props}>
    {children}
  </div>
);

const CardFooter: FC<CardFooterProps> = ({ children, justify, className, ...props }) => (
  <div
    className={clsx(styles.footer, className)}
    style={justify ? ({ '--card-footer-justify': justify } as CSSProperties) : undefined}
    {...props}
  >
    {children}
  </div>
);

CardHeader.displayName = 'Card.Header';
CardBody.displayName = 'Card.Body';
CardFooter.displayName = 'Card.Footer';

const CardBase = forwardRef<HTMLDivElement, CardProps>(
  ({ children, variant = 'default', padding, hoverable = false, className, ...props }, ref) => {
    const hasPadding = Boolean(padding);
    const paddingStyle =
      hasPadding && typeof padding === 'string' ? ({ '--card-padding': padding } as CSSProperties) : undefined;

    return (
      <div
        ref={ref}
        className={clsx(styles.card, className)}
        data-variant={variant}
        data-hoverable={hoverable ? 'true' : undefined}
        data-has-padding={hasPadding ? 'true' : undefined}
        style={paddingStyle}
        {...props}
      >
        {children}
      </div>
    );
  },
);

CardBase.displayName = 'Card';

(CardBase as CardComponent).Header = CardHeader;
(CardBase as CardComponent).Body = CardBody;
(CardBase as CardComponent).Footer = CardFooter;

export const Card = CardBase as CardComponent;
